import { type RefObject, useEffect, useState } from "react";

/**
 * Track whether an element is at least `minWidth` wide.
 *
 * Prefer the `*-stitch:` container-query variants for styling. This hook exists
 * for the cases a container query cannot express, such as swapping a
 * `react-resizable-panels` group between horizontal and vertical orientation —
 * that is a prop, not a class.
 *
 * Returns `false` until the first non-zero measurement, so server-rendered
 * markup starts from the narrow layout. Zero-width readings are ignored — tabs
 * and other `display: none` hosts report 0 while hidden, and treating those as
 * "narrow" would permanently flip layout the moment the user leaves the tab.
 */
export function useContainerWiderThan(
  ref: RefObject<HTMLElement | null>,
  minWidth: number,
): boolean {
  const [isWider, setIsWider] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (element == null) return;

    const update = (width: number) => {
      if (width <= 0) return;
      setIsWider(width >= minWidth);
    };

    update(element.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        update(entry.contentRect.width);
      }
    });
    observer.observe(element);

    return function cleanup() {
      observer.disconnect();
    };
  }, [ref, minWidth]);

  return isWider;
}
